// Utility function to combine classes
export const cn = (...classes) => classes.filter(Boolean).join(" ");

/** Horizontal inset from the viewport — must match `<Header />` `padding-inline`. */
export const headerShellHorizontalInsetClass = "px-2.5";

/** Same as `<Header />` nav links — use for gallery chrome so counters match. */
export const headerNavTextClass = "no-underline font-times text-sm";

/**
 * Gallery “1 / 12” counter: same face as nav, slightly smaller em size so lining
 * figures don’t read larger than the mixed-case header links.
 */
export const galleryCounterTextClass =
  "no-underline font-times text-[0.8125rem] leading-none tabular-nums";

const DEFAULT_BUNNY_ZONE =
  process.env.GATSBY_BUNNY_IMAGE_ZONE || "https://kurtbauer.b-cdn.net";
const DEFAULT_QUALITY = Number(process.env.GATSBY_BUNNY_IMAGE_QUALITY) || 80;

export const getRawImageUrl = (image) => {
  if (!image) return null;

  if (typeof image === "string") return image;

  return image.url || image.localFile?.url || null;
};

/**
 * Map a Strapi / DigitalOcean asset URL to the Bunny pull zone origin only.
 * Transform params (`width`, `quality`, `format`) are ignored on DO Spaces — they
 * must be applied on the Bunny CDN host via `toBunnyImageUrl` or `BunnyImage`.
 */
export const toBunnyOriginUrl = (src) => {
  if (!src) return null;

  const zone = (
    process.env.GATSBY_BUNNY_IMAGE_ZONE || DEFAULT_BUNNY_ZONE
  ).replace(/\/$/, "");

  let pathname = src;
  if (src.startsWith("http")) {
    try {
      pathname = new URL(src).pathname;
    } catch {
      pathname = src;
    }
  }

  const normalizedPath =
    (pathname.startsWith("/") ? pathname : `/${pathname}`).replace(
      /^\/kurtbauer(?=\/|$)/,
      "",
    ) || "/";

  return `${zone}${normalizedPath}`;
};

export const toBunnyImageUrl = (src, options = {}) => {
  if (!src) return null;

  const origin = toBunnyOriginUrl(src);
  if (!origin) return null;

  const transformUrl = new URL(origin);
  const params = transformUrl.searchParams;

  if (options.width) params.set("width", String(options.width));
  if (options.height) params.set("height", String(options.height));
  if (options.quality || DEFAULT_QUALITY) {
    params.set("quality", String(options.quality || DEFAULT_QUALITY));
  }
  if (options.crop) params.set("crop", options.crop);
  if (options.aspectRatio)
    params.set("aspect_ratio", String(options.aspectRatio));
  if (options.format) params.set("format", options.format);

  return transformUrl.toString();
};

function fisherYatesShuffle(items) {
  const arr = [...items];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

/** Strapi `Position` when set; otherwise `null` (empty / non-numeric / 0). */
export function getProjectPositionValue(project) {
  const raw = project?.Position;
  if (raw == null || raw === "") return null;
  const n = Number(raw);
  if (!Number.isFinite(n) || n === 0) return null;
  return n;
}

/**
 * Index project list: ascending `Position` (excluding 0), then unpositioned
 * (missing, invalid, or 0) shuffled.
 */
export function orderProjectsByPosition(projects) {
  const positioned = [];
  const unpositioned = [];

  for (const project of projects) {
    const pos = getProjectPositionValue(project);
    if (pos === null) {
      unpositioned.push(project);
    } else {
      positioned.push({ project, pos });
    }
  }

  positioned.sort((a, b) => {
    if (a.pos !== b.pos) return a.pos - b.pos;
    return String(a.project.id).localeCompare(String(b.project.id));
  });

  return [
    ...positioned.map(({ project }) => project),
    ...fisherYatesShuffle(unpositioned),
  ];
}
